import { supabase } from '@/integrations/supabase/client';

const USERNAME_REGEX = /^[a-z0-9_]+$/;
const MIN_LENGTH = 3;
const MAX_LENGTH = 20;

export interface UsernameValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validate username format rules (synchronous, no DB check).
 * - Lowercase letters, numbers, underscores only
 * - 3-20 characters
 * - Cannot contain user's first or last name
 */
export function validateUsernameFormat(
  username: string,
  displayName?: string
): UsernameValidationResult {
  const u = username.trim();

  if (u.length < MIN_LENGTH) {
    return { valid: false, error: `Username must be at least ${MIN_LENGTH} characters` };
  }
  if (u.length > MAX_LENGTH) {
    return { valid: false, error: `Username must be ${MAX_LENGTH} characters or fewer` };
  }
  if (!USERNAME_REGEX.test(u)) {
    return { valid: false, error: 'Only lowercase letters, numbers, and underscores allowed' };
  }

  // Check if username contains user's first or last name
  if (displayName) {
    const nameParts = displayName
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter((p) => p.length >= 2); // ignore single-char name parts

    for (const part of nameParts) {
      if (u.includes(part)) {
        return { valid: false, error: `Username cannot contain your name ("${part}")` };
      }
    }
  }

  return { valid: true };
}

/**
 * Check if username is available in the database.
 */
export async function checkUsernameAvailable(username: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('check_username_available', {
    p_username: username.trim().toLowerCase(),
  });
  if (error) return false;
  return data === true;
}

/**
 * Sanitize input to only allow valid username characters.
 */
export function sanitizeUsername(input: string): string {
  return input.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, MAX_LENGTH);
}
